import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Receipt, Plus, Loader2, Search, Download, Eye, Edit, Trash2, Send,
  CreditCard, Calendar, AlertTriangle, Building, X, FileText, Banknote, Filter, History, Users,
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';
import AttachmentPreviewModal from '../components/AttachmentPreviewModal';

const STATUS_BADGE = (s, isOverdue) => {
  if (s === 'Paid') return <Badge className="bg-emerald-100 text-emerald-800 border-emerald-300">Paid</Badge>;
  if (s === 'Partially Paid') return <Badge className="bg-blue-100 text-blue-800 border-blue-300">Partial</Badge>;
  if (s === 'Overdue' || isOverdue) return <Badge variant="destructive">Overdue</Badge>;
  if (s === 'Sent') return <Badge className="bg-violet-100 text-violet-800 border-violet-300">Sent</Badge>;
  if (s === 'Draft') return <Badge variant="secondary">Draft</Badge>;
  if (s === 'Cancelled') return <Badge variant="outline">Cancelled</Badge>;
  return <Badge className="bg-amber-100 text-amber-800 border-amber-300">Unpaid</Badge>;
};

const newLine = () => ({ description: '', quantity: 1, unit_price: 0 });

const EMPTY_FORM = {
  office_id: '',
  direction: 'Outgoing',
  invoice_number: '',
  party_id: null,
  party_name: '',
  party_email: '',
  party_phone: '',
  party_address: '',
  party_tax_id: '',
  issue_date: new Date().toISOString().split('T')[0],
  due_date: '',
  currency: 'TZS',
  apply_vat: false,
  tax_percent: 0,
  payment_terms: 'Net 30 days',
  notes: '',
  line_items: [newLine()],
};

const Invoices = () => {
  const { getAuthHeaders, currentOffice, offices, isSuperAdmin } = useAuth();
  const API_URL = process.env.REACT_APP_BACKEND_URL;
  const isSA = isSuperAdmin();

  const [tab, setTab] = useState('Outgoing');
  const [invoices, setInvoices] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [overdueOnly, setOverdueOnly] = useState(false);
  const [officeFilter, setOfficeFilter] = useState('all');
  const [allOffices, setAllOffices] = useState([]);

  // Pagination
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  // Reset to page 1 when filters change
  useEffect(() => { setPage(1); }, [tab, search, statusFilter, overdueOnly, officeFilter, pageSize]);

  const [showFormModal, setShowFormModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [formData, setFormData] = useState(EMPTY_FORM);
  const [submitting, setSubmitting] = useState(false);

  const [showDetail, setShowDetail] = useState(false);
  const [selectedInvoice, setSelectedInvoice] = useState(null);

  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [paymentForm, setPaymentForm] = useState({
    amount: '', payment_date: new Date().toISOString().split('T')[0], method: 'Bank Transfer', reference: '', notes: '',
  });
  const [paymentProofFile, setPaymentProofFile] = useState(null);

  const [previewConfig, setPreviewConfig] = useState(null);
  const [paymentLogInvoice, setPaymentLogInvoice] = useState(null);
  const [officeConfig, setOfficeConfig] = useState({ currency: 'TZS', vat_percent: 18.0 });

  // Party (customer / vendor) directory state
  const [parties, setParties] = useState([]);
  const [showPartyModal, setShowPartyModal] = useState(false);
  const [partyForm, setPartyForm] = useState({
    kind: 'Customer', name: '', email: '', phone: '', address: '', tax_id: '', notes: '',
  });
  const [partySubmitting, setPartySubmitting] = useState(false);
  const [editingParty, setEditingParty] = useState(null);
  const [showDirectoryModal, setShowDirectoryModal] = useState(false);
  const [directoryTab, setDirectoryTab] = useState('Customer');
  const [directorySearch, setDirectorySearch] = useState('');

  const fetchParties = useCallback(async () => {
    try {
      const headers = getAuthHeaders();
      const res = await fetch(`${API_URL}/api/invoice-parties/`, { headers });
      if (res.ok) setParties(await res.json());
    } catch (e) { console.error(e); }
  }, [API_URL, getAuthHeaders]);

  const fetchOfficeConfig = useCallback(async (officeIdParam) => {
    try {
      const params = new URLSearchParams();
      if (officeIdParam) params.append('office_id', officeIdParam);
      const res = await fetch(`${API_URL}/api/invoices/office-config?${params.toString()}`, {
        headers: getAuthHeaders(),
      });
      if (res.ok) {
        const data = await res.json();
        setOfficeConfig(data);
        return data;
      }
    } catch (e) { console.error(e); }
    return null;
  }, [API_URL, getAuthHeaders]);

  const fetchOffices = useCallback(async () => {
    if (!isSA) return;
    try {
      const res = await fetch(`${API_URL}/api/offices/`, { headers: getAuthHeaders() });
      if (res.ok) setAllOffices(await res.json());
    } catch (e) { console.error(e); }
  }, [API_URL, getAuthHeaders, isSA]);

  const fetchSummary = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/invoices/summary`, { headers: getAuthHeaders() });
      if (res.ok) setSummary(await res.json());
    } catch (e) { console.error(e); }
  }, [API_URL, getAuthHeaders]);

  const fetchInvoices = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.append('direction', tab);
      if (search.trim()) params.append('search', search.trim());
      if (statusFilter !== 'all') params.append('status', statusFilter);
      if (overdueOnly) params.append('overdue_only', 'true');
      if (isSA && officeFilter !== 'all') params.append('office_id', officeFilter);
      const res = await fetch(`${API_URL}/api/invoices/?${params.toString()}`, { headers: getAuthHeaders() });
      if (res.ok) {
        setInvoices(await res.json());
      } else {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || 'Failed to load invoices');
      }
    } catch (e) {
      console.error(e);
      toast.error('Network error');
    } finally {
      setLoading(false);
    }
  }, [API_URL, getAuthHeaders, tab, search, statusFilter, overdueOnly, isSA, officeFilter]);

  useEffect(() => { fetchOffices(); fetchSummary(); fetchOfficeConfig(); fetchParties(); }, [fetchOffices, fetchSummary, fetchOfficeConfig, fetchParties]);
  useEffect(() => { fetchInvoices(); }, [fetchInvoices]);

  const resetForm = () => {
    setFormData({
      ...EMPTY_FORM,
      direction: tab,
      office_id: isSA ? '' : (currentOffice?.id || (offices?.[0]?.id) || ''),
      currency: officeConfig.currency || 'TZS',
      apply_vat: false,
      tax_percent: 0,
    });
    setEditing(null);
  };

  const openCreate = () => {
    resetForm();
    setShowFormModal(true);
  };

  const openEdit = (inv) => {
    setEditing(inv);
    setFormData({
      office_id: inv.office_id,
      direction: inv.direction,
      invoice_number: inv.invoice_number,
      party_id: null,
      party_name: inv.party_name || '',
      party_email: inv.party_email || '',
      party_phone: inv.party_phone || '',
      party_address: inv.party_address || '',
      party_tax_id: inv.party_tax_id || '',
      issue_date: inv.issue_date || '',
      due_date: inv.due_date || '',
      currency: inv.currency || 'TZS',
      apply_vat: (inv.tax_percent || 0) > 0,
      tax_percent: inv.tax_percent || 0,
      payment_terms: inv.payment_terms || '',
      notes: inv.notes || '',
      line_items: inv.line_items?.length ? inv.line_items.map((l) => ({ ...l })) : [newLine()],
    });
    setShowFormModal(true);
  };

  const computedTotals = useMemo(() => {
    const items = formData.line_items || [];
    const subtotal = items.reduce((s, l) => s + (Number(l.quantity) || 0) * (Number(l.unit_price) || 0), 0);
    const tax = subtotal * (Number(formData.tax_percent) || 0) / 100;
    return { subtotal, tax, total: subtotal + tax };
  }, [formData.line_items, formData.tax_percent]);

  // Pagination derivation — operates on the filtered list returned from the backend
  const totalCount = invoices.length;
  const totalPages = Math.max(1, Math.ceil(totalCount / pageSize));
  const safePage = Math.min(page, totalPages);
  const paginatedInvoices = useMemo(() => {
    const start = (safePage - 1) * pageSize;
    return invoices.slice(start, start + pageSize);
  }, [invoices, safePage, pageSize]);
  const rangeStart = totalCount === 0 ? 0 : (safePage - 1) * pageSize + 1;
  const rangeEnd = Math.min(safePage * pageSize, totalCount);

  const updateLine = (idx, field, value) => {
    const lines = [...formData.line_items];
    lines[idx] = { ...lines[idx], [field]: value };
    setFormData({ ...formData, line_items: lines });
  };

  const addLine = () => setFormData({ ...formData, line_items: [...formData.line_items, newLine()] });
  const removeLine = (idx) => setFormData({ ...formData, line_items: formData.line_items.filter((_, i) => i !== idx) });

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.party_name.trim()) { toast.error('Party name is required'); return; }
    if (formData.direction === 'Incoming' && !formData.invoice_number.trim()) {
      toast.error('Vendor invoice number is required'); return;
    }
    if (formData.direction === 'Outgoing' && !editing && !formData.line_items.some((l) => l.description.trim())) {
      toast.error('Please add at least one line item'); return;
    }
    if (isSA && !editing && !formData.office_id) { toast.error('Please select an office'); return; }

    setSubmitting(true);
    try {
      const payload = {
        ...(editing ? {} : {
          direction: formData.direction,
          office_id: isSA ? formData.office_id : undefined,
          invoice_number: formData.direction === 'Incoming' ? formData.invoice_number.trim() : undefined,
        }),
        party_name: formData.party_name.trim(),
        party_email: formData.party_email || null,
        party_phone: formData.party_phone || null,
        party_address: formData.party_address || null,
        party_tax_id: formData.party_tax_id || null,
        issue_date: formData.issue_date,
        due_date: formData.due_date || null,
        currency: formData.currency,
        tax_percent: Number(formData.tax_percent) || 0,
        payment_terms: formData.payment_terms || null,
        notes: formData.notes || null,
        line_items: formData.line_items
          .filter((l) => l.description?.trim())
          .map((l) => ({ description: l.description.trim(), quantity: Number(l.quantity) || 0, unit_price: Number(l.unit_price) || 0 })),
      };

      const headers = { ...getAuthHeaders(), 'Content-Type': 'application/json' };
      const url = editing ? `${API_URL}/api/invoices/${editing.id}` : `${API_URL}/api/invoices/`;
      const res = await fetch(url, { method: editing ? 'PUT' : 'POST', headers, body: JSON.stringify(payload) });
      if (res.ok) {
        toast.success(editing ? 'Invoice updated' : 'Invoice created');
        setShowFormModal(false);
        resetForm();
        fetchInvoices();
        fetchSummary();
      } else {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || 'Failed to save');
      }
    } catch (e) {
      toast.error('Network error');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (inv) => {
    if (!window.confirm(`Delete invoice ${inv.invoice_number}? This cannot be undone.`)) return;
    try {
      const res = await fetch(`${API_URL}/api/invoices/${inv.id}`, { method: 'DELETE', headers: getAuthHeaders() });
      if (res.ok) {
        toast.success('Invoice deleted');
        fetchInvoices();
        fetchSummary();
      } else { toast.error('Failed to delete'); }
    } catch { toast.error('Network error'); }
  };

  const handleSend = async (inv) => {
    try {
      const res = await fetch(`${API_URL}/api/invoices/${inv.id}/send`, { method: 'PUT', headers: getAuthHeaders() });
      if (res.ok) {
        toast.success('Invoice marked as sent');
        fetchInvoices();
        fetchSummary();
      } else {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || 'Failed to mark as sent');
      }
    } catch { toast.error('Network error'); }
  };

  const downloadPdf = async (inv) => {
    try {
      const headers = getAuthHeaders();
      delete headers['Content-Type'];
      const res = await fetch(`${API_URL}/api/invoices/${inv.id}/pdf`, { headers });
      if (!res.ok) { toast.error('Failed to download PDF'); return; }
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = `Invoice_${inv.invoice_number}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch { toast.error('Download error'); }
  };

  const previewPdf = (inv) => {
    setPreviewConfig({
      title: `Invoice ${inv.invoice_number} — ${inv.party_name}`,
      downloadName: `Invoice_${inv.invoice_number}.pdf`,
      fetchUrl: `${API_URL}/api/invoices/${inv.id}/pdf`,
      mime: 'application/pdf',
    });
  };

  const openDetail = async (inv) => {
    try {
      const res = await fetch(`${API_URL}/api/invoices/${inv.id}`, { headers: getAuthHeaders() });
      if (res.ok) {
        setSelectedInvoice(await res.json());
        setShowDetail(true);
      }
    } catch (e) { console.error(e); }
  };

  const openPaymentLog = async (inv) => {
    try {
      const res = await fetch(`${API_URL}/api/invoices/${inv.id}`, { headers: getAuthHeaders() });
      if (res.ok) {
        setPaymentLogInvoice(await res.json());
      } else {
        setPaymentLogInvoice(inv);
      }
    } catch (e) {
      setPaymentLogInvoice(inv);
    }
  };

  const deletePaymentFromLog = async (paymentId) => {
    if (!paymentLogInvoice) return;
    if (!window.confirm('Remove this payment record?')) return;
    try {
      const res = await fetch(`${API_URL}/api/invoices/${paymentLogInvoice.id}/payments/${paymentId}`, {
        method: 'DELETE', headers: getAuthHeaders(),
      });
      if (res.ok) {
        const updated = await res.json();
        toast.success('Payment removed');
        setPaymentLogInvoice(updated);
        fetchInvoices();
        fetchSummary();
      }
    } catch { toast.error('Network error'); }
  };

  const openPaymentModal = (inv) => {
    setSelectedInvoice(inv);
    setPaymentForm({
      amount: String(inv.balance || 0),
      payment_date: new Date().toISOString().split('T')[0],
      method: 'Bank Transfer', reference: '', notes: '',
    });
    setPaymentProofFile(null);
    setShowPaymentModal(true);
  };

  const recordPayment = async (e) => {
    e.preventDefault();
    if (!selectedInvoice) return;
    const amt = Number(paymentForm.amount);
    if (!amt || amt <= 0) { toast.error('Enter a valid amount'); return; }
    setSubmitting(true);
    try {
      const headers = { ...getAuthHeaders(), 'Content-Type': 'application/json' };
      const body = {
        amount: amt,
        payment_date: paymentForm.payment_date,
        method: paymentForm.method || null,
        reference: paymentForm.reference || null,
        notes: paymentForm.notes || null,
      };
      if (paymentProofFile) {
        const reader = new FileReader();
        const base64 = await new Promise((resolve, reject) => {
          reader.onload = () => resolve(reader.result);
          reader.onerror = reject;
          reader.readAsDataURL(paymentProofFile);
        });
        body.proof_pdf = base64;
        body.proof_filename = paymentProofFile.name;
        body.proof_mime = paymentProofFile.type || 'application/octet-stream';
      }
      const res = await fetch(`${API_URL}/api/invoices/${selectedInvoice.id}/payments`, {
        method: 'POST', headers,
        body: JSON.stringify(body),
      });
      if (res.ok) {
        const updated = await res.json();
        toast.success('Payment recorded');
        setShowPaymentModal(false);
        setSelectedInvoice(updated);
        fetchInvoices();
        fetchSummary();
      } else {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || 'Failed to record payment');
      }
    } catch { toast.error('Network error'); }
    finally { setSubmitting(false); }
  };

  const deletePayment = async (paymentId) => {
    if (!selectedInvoice) return;
    if (!window.confirm('Remove this payment record?')) return;
    try {
      const res = await fetch(`${API_URL}/api/invoices/${selectedInvoice.id}/payments/${paymentId}`, {
        method: 'DELETE', headers: getAuthHeaders(),
      });
      if (res.ok) {
        const updated = await res.json();
        toast.success('Payment removed');
        setSelectedInvoice(updated);
        fetchInvoices();
        fetchSummary();
      }
    } catch { toast.error('Network error'); }
  };

  const fmt = (cur, n) => `${cur || ''} ${(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

  const sumBucket = summary ? (tab === 'Outgoing' ? summary.outgoing : summary.incoming) : null;

  return (
    <div className="p-6 space-y-6" data-testid="invoices-page">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Receipt className="h-6 w-6 text-amber-600" />
            Invoices
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            Issue invoices to clients and log incoming vendor invoices with payment tracking.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            onClick={() => setShowDirectoryModal(true)}
            className="gap-2"
            data-testid="open-directory-btn"
          >
            <Users className="h-4 w-4" /> Customers & Vendors
          </Button>
          <Button onClick={openCreate} className="gap-2" data-testid="add-invoice-btn">
            <Plus className="h-4 w-4" /> New Invoice
          </Button>
        </div>
      </div>

      {/* Direction tabs — prominent segmented control above analytics */}
      <div className="flex justify-center sm:justify-start">
        <div className="inline-flex rounded-lg border-2 border-primary/30 bg-white shadow-sm overflow-hidden" data-testid="invoice-direction-tabs">
          <button
            type="button"
            onClick={() => setTab('Outgoing')}
            className={`flex items-center gap-2 px-5 sm:px-7 py-2.5 text-sm font-semibold transition-colors ${
              tab === 'Outgoing'
                ? 'bg-primary text-primary-foreground'
                : 'bg-white text-muted-foreground hover:bg-primary/10 hover:text-primary'
            }`}
            data-testid="tab-outgoing"
          >
            <Send className="h-4 w-4" />
            Outgoing
            {summary?.outgoing?.count > 0 && (
              <span className={`ml-1 inline-flex items-center justify-center min-w-[22px] h-[20px] px-1.5 rounded-full text-[11px] font-bold ${
                tab === 'Outgoing' ? 'bg-white/25 text-primary-foreground' : 'bg-primary/15 text-primary'
              }`}>
                {summary.outgoing.count}
              </span>
            )}
          </button>
          <button
            type="button"
            onClick={() => setTab('Incoming')}
            className={`flex items-center gap-2 px-5 sm:px-7 py-2.5 text-sm font-semibold transition-colors border-l border-primary/30 ${
              tab === 'Incoming'
                ? 'bg-primary text-primary-foreground'
                : 'bg-white text-muted-foreground hover:bg-primary/10 hover:text-primary'
            }`}
            data-testid="tab-incoming"
          >
            <Banknote className="h-4 w-4" />
            Incoming
            {summary?.incoming?.count > 0 && (
              <span className={`ml-1 inline-flex items-center justify-center min-w-[22px] h-[20px] px-1.5 rounded-full text-[11px] font-bold ${
                tab === 'Incoming' ? 'bg-white/25 text-primary-foreground' : 'bg-primary/15 text-primary'
              }`}>
                {summary.incoming.count}
              </span>
            )}
          </button>
        </div>
      </div>

      {sumBucket && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <Card><CardContent className="p-4">
            <div className="text-xs text-muted-foreground">{tab === 'Outgoing' ? 'Total Billed' : 'Total Due'}</div>
            <div className="text-xl font-bold mt-1">{fmt('TZS', tab === 'Outgoing' ? sumBucket.total_billed : sumBucket.total_due)}</div>
          </CardContent></Card>
          <Card><CardContent className="p-4">
            <div className="text-xs text-muted-foreground">Total Paid</div>
            <div className="text-xl font-bold mt-1 text-emerald-600">{fmt('TZS', sumBucket.total_paid)}</div>
          </CardContent></Card>
          <Card><CardContent className="p-4">
            <div className="text-xs text-muted-foreground">Outstanding</div>
            <div className="text-xl font-bold mt-1 text-amber-600">{fmt('TZS', sumBucket.outstanding)}</div>
          </CardContent></Card>
          <Card><CardContent className="p-4">
            <div className="text-xs text-muted-foreground">Overdue Count</div>
            <div className="text-xl font-bold mt-1 text-red-600">{sumBucket.overdue}</div>
          </CardContent></Card>
        </div>
      )}

      <Card>
        <CardContent className="p-4 flex flex-wrap items-center gap-3">
          <div className="relative flex-1 min-w-[240px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input placeholder="Search invoice #, party, email..." value={search} onChange={(e) => setSearch(e.target.value)} className="pl-9" data-testid="invoice-search" />
          </div>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-[160px]" data-testid="invoice-status-filter">
              <SelectValue placeholder="Status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All statuses</SelectItem>
              <SelectItem value="Draft">Draft</SelectItem>
              <SelectItem value="Sent">Sent</SelectItem>
              <SelectItem value="Unpaid">Unpaid</SelectItem>
              <SelectItem value="Partially Paid">Partially Paid</SelectItem>
              <SelectItem value="Paid">Paid</SelectItem>
              <SelectItem value="Overdue">Overdue</SelectItem>
              <SelectItem value="Cancelled">Cancelled</SelectItem>
            </SelectContent>
          </Select>
          {isSA && (
            <Select value={officeFilter} onValueChange={setOfficeFilter}>
              <SelectTrigger className="w-[200px]"><SelectValue placeholder="Office" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All offices</SelectItem>
                {allOffices.map((o) => (<SelectItem key={o.id} value={o.id}>{o.name}</SelectItem>))}
              </SelectContent>
            </Select>
          )}
          <Button variant={overdueOnly ? 'default' : 'outline'} onClick={() => setOverdueOnly((v) => !v)} className="gap-2">
            <Filter className="h-4 w-4" /> {overdueOnly ? 'Showing overdue' : 'Show overdue'}
          </Button>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="p-12 flex items-center justify-center text-muted-foreground">
              <Loader2 className="h-5 w-5 animate-spin mr-2" /> Loading invoices...
            </div>
          ) : invoices.length === 0 ? (
            <div className="p-12 text-center text-muted-foreground">
              <Receipt className="h-10 w-10 mx-auto mb-3 opacity-40" />
              <p className="font-medium">No {tab.toLowerCase()} invoices yet</p>
              <p className="text-sm">Click "New Invoice" to create your first one.</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-muted/40 text-muted-foreground">
                  <tr className="text-left">
                    <th className="px-4 py-3 font-medium">Invoice #</th>
                    <th className="px-4 py-3 font-medium">{tab === 'Outgoing' ? 'Client' : 'Vendor'}</th>
                    {isSA && <th className="px-4 py-3 font-medium">Office</th>}
                    <th className="px-4 py-3 font-medium">Issued</th>
                    <th className="px-4 py-3 font-medium text-right">Total</th>
                    <th className="px-4 py-3 font-medium text-right">Balance</th>
                    <th className="px-4 py-3 font-medium">Status</th>
                    <th className="px-4 py-3 font-medium text-right">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {paginatedInvoices.map((inv) => (
                    <tr key={inv.id} className="border-t hover:bg-muted/20" data-testid={`invoice-row-${inv.id}`}>
                      <td className="px-4 py-3 font-mono text-xs">
                        <button className="hover:underline text-blue-600" onClick={() => openDetail(inv)}>{inv.invoice_number}</button>
                      </td>
                      <td className="px-4 py-3">
                        <div className="font-medium">{inv.party_name}</div>
                        {inv.party_email && <div className="text-xs text-muted-foreground">{inv.party_email}</div>}
                      </td>
                      {isSA && (
                        <td className="px-4 py-3"><span className="inline-flex items-center gap-1 text-xs"><Building className="h-3 w-3 opacity-60" />{inv.office_name || '—'}</span></td>
                      )}
                      <td className="px-4 py-3 text-xs"><Calendar className="h-3 w-3 inline opacity-60 mr-1" />{inv.issue_date}</td>
                      <td className="px-4 py-3 text-right">{fmt(inv.currency, inv.total_amount)}</td>
                      <td className={`px-4 py-3 text-right font-medium ${inv.balance > 0 ? 'text-amber-700' : 'text-emerald-600'}`}>{fmt(inv.currency, inv.balance)}</td>
                      <td className="px-4 py-3">{STATUS_BADGE(inv.status, inv.is_overdue)}</td>
                      <td className="px-4 py-3">
                        <div className="flex items-center justify-end gap-1">
                          <Button
                            variant="ghost" size="sm"
                            onClick={() => openPaymentLog(inv)}
                            title="View payment log"
                            data-testid={`payment-log-${inv.id}`}
                            className="relative"
                          >
                            <History className="h-4 w-4" />
                            {(inv.payments?.length || 0) > 0 && (
                              <span className="ml-1 inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full bg-emerald-100 text-emerald-700 text-[10px] font-bold">
                                {inv.payments.length}
                              </span>
                            )}
                          </Button>
                          {inv.direction === 'Outgoing' && (
                            <>
                              <Button variant="ghost" size="sm" onClick={() => previewPdf(inv)} title="Preview PDF" data-testid={`preview-invoice-${inv.id}`}>
                                <Eye className="h-4 w-4" />
                              </Button>
                              <Button variant="ghost" size="sm" onClick={() => downloadPdf(inv)} title="Download PDF" data-testid={`download-invoice-${inv.id}`}>
                                <Download className="h-4 w-4" />
                              </Button>
                              {inv.status === 'Draft' && (
                                <Button variant="ghost" size="sm" onClick={() => handleSend(inv)} className="text-violet-600" title="Mark as Sent" data-testid={`send-invoice-${inv.id}`}>
                                  <Send className="h-4 w-4" />
                                </Button>
                              )}
                            </>
                          )}
                          {inv.balance > 0 && inv.status !== 'Cancelled' && (
                            <Button variant="ghost" size="sm" onClick={() => openPaymentModal(inv)} className="text-emerald-700" title="Record Payment" data-testid={`pay-invoice-${inv.id}`}>
                              <CreditCard className="h-4 w-4" />
                            </Button>
                          )}
                          {isSA && (
                            <>
                              <Button variant="ghost" size="sm" onClick={() => openEdit(inv)} title="Edit"><Edit className="h-4 w-4" /></Button>
                              <Button variant="ghost" size="sm" onClick={() => handleDelete(inv)} className="text-red-600" title="Delete"><Trash2 className="h-4 w-4" /></Button>
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          {!loading && totalCount > 0 && (
            <div className="flex flex-col sm:flex-row items-center justify-between gap-3 px-4 py-3 border-t bg-muted/20" data-testid="invoice-pagination">
              <div className="flex items-center gap-3 text-xs text-muted-foreground">
                <span data-testid="pagination-range">
                  Showing <span className="font-semibold text-foreground">{rangeStart}</span>–
                  <span className="font-semibold text-foreground">{rangeEnd}</span> of{' '}
                  <span className="font-semibold text-foreground">{totalCount}</span>
                </span>
                <div className="flex items-center gap-1.5">
                  <span>Rows:</span>
                  <Select value={String(pageSize)} onValueChange={(v) => setPageSize(Number(v))}>
                    <SelectTrigger className="h-7 w-[70px] text-xs" data-testid="pagination-pagesize">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="10">10</SelectItem>
                      <SelectItem value="25">25</SelectItem>
                      <SelectItem value="50">50</SelectItem>
                      <SelectItem value="100">100</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="flex items-center gap-1">
                <Button
                  variant="outline" size="sm"
                  className="h-8 px-2"
                  disabled={safePage <= 1}
                  onClick={() => setPage(1)}
                  data-testid="pagination-first"
                  title="First page"
                >
                  «
                </Button>
                <Button
                  variant="outline" size="sm"
                  className="h-8 px-2"
                  disabled={safePage <= 1}
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  data-testid="pagination-prev"
                  title="Previous"
                >
                  ‹ Prev
                </Button>
                {(() => {
                  // Build a compact page-number list (max 5 buttons centered around current)
                  const pages = [];
                  const windowSize = 5;
                  let start = Math.max(1, safePage - Math.floor(windowSize / 2));
                  let end = Math.min(totalPages, start + windowSize - 1);
                  if (end - start + 1 < windowSize) {
                    start = Math.max(1, end - windowSize + 1);
                  }
                  for (let p = start; p <= end; p++) pages.push(p);
                  return pages.map((p) => (
                    <Button
                      key={p}
                      variant={p === safePage ? 'default' : 'outline'}
                      size="sm"
                      className="h-8 w-8 p-0 text-xs"
                      onClick={() => setPage(p)}
                      data-testid={`pagination-page-${p}`}
                    >
                      {p}
                    </Button>
                  ));
                })()}
                <Button
                  variant="outline" size="sm"
                  className="h-8 px-2"
                  disabled={safePage >= totalPages}
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                  data-testid="pagination-next"
                  title="Next"
                >
                  Next ›
                </Button>
                <Button
                  variant="outline" size="sm"
                  className="h-8 px-2"
                  disabled={safePage >= totalPages}
                  onClick={() => setPage(totalPages)}
                  data-testid="pagination-last"
                  title="Last page"
                >
                  »
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Create / Edit Modal */}
      <Dialog open={showFormModal} onOpenChange={(o) => { if (!o) { setShowFormModal(false); resetForm(); } }}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editing ? `Edit Invoice ${editing.invoice_number}` : `Create ${formData.direction} Invoice`}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            {!editing && (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Direction *</Label>
                  <Select value={formData.direction} onValueChange={(v) => setFormData({ ...formData, direction: v, party_id: null })}>
                    <SelectTrigger data-testid="form-direction"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Outgoing">Outgoing (we issue)</SelectItem>
                      <SelectItem value="Incoming">Incoming (vendor sent)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                {isSA && (
                  <div className="space-y-2">
                    <Label>Office *</Label>
                    <Select value={formData.office_id} onValueChange={async (v) => {
                      const cfg = await fetchOfficeConfig(v);
                      setFormData((prev) => ({
                        ...prev,
                        office_id: v,
                        currency: cfg?.currency || prev.currency,
                      }));
                    }}>
                      <SelectTrigger><SelectValue placeholder="Select office" /></SelectTrigger>
                      <SelectContent>
                        {allOffices.map((o) => (<SelectItem key={o.id} value={o.id}>{o.name}</SelectItem>))}
                      </SelectContent>
                    </Select>
                  </div>
                )}
              </div>
            )}

            {formData.direction === 'Incoming' && (
              <div className="space-y-2">
                <Label>Vendor Invoice Number *</Label>
                <Input value={formData.invoice_number} onChange={(e) => setFormData({ ...formData, invoice_number: e.target.value })} placeholder="e.g. INV-2026-001" data-testid="form-invoice-number" disabled={!!editing} />
              </div>
            )}

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="sm:col-span-2 space-y-2">
                <Label>
                  {formData.direction === 'Outgoing' ? 'Select Customer' : 'Select Vendor'}
                </Label>
                <div className="flex gap-2">
                  <Select
                    value={formData.party_id || 'manual'}
                    onValueChange={(v) => {
                      if (v === 'manual') {
                        setFormData({ ...formData, party_id: null });
                        return;
                      }
                      const p = parties.find((x) => x.id === v);
                      if (p) {
                        setFormData({
                          ...formData,
                          party_id: p.id,
                          party_name: p.name || '',
                          party_email: p.email || '',
                          party_phone: p.phone || '',
                          party_address: p.address || '',
                          party_tax_id: p.tax_id || '',
                        });
                      }
                    }}
                  >
                    <SelectTrigger className="flex-1" data-testid="form-party-select">
                      <SelectValue placeholder={
                        formData.direction === 'Outgoing'
                          ? 'Choose a saved customer or enter manually'
                          : 'Choose a saved vendor or enter manually'
                      } />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="manual">— Enter manually —</SelectItem>
                      {parties
                        .filter((p) => p.kind === (formData.direction === 'Outgoing' ? 'Customer' : 'Vendor'))
                        .map((p) => (
                          <SelectItem key={p.id} value={p.id}>
                            {p.name}{p.tax_id ? ` · TIN ${p.tax_id}` : ''}
                          </SelectItem>
                        ))}
                    </SelectContent>
                  </Select>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => {
                      setPartyForm({
                        kind: formData.direction === 'Outgoing' ? 'Customer' : 'Vendor',
                        name: '', email: '', phone: '', address: '', tax_id: '', notes: '',
                      });
                      setShowPartyModal(true);
                    }}
                    className="gap-1 shrink-0"
                    data-testid="form-party-new"
                  >
                    <Plus className="h-4 w-4" /> New
                  </Button>
                </div>
                <p className="text-[11px] text-muted-foreground">
                  Selecting a saved {formData.direction === 'Outgoing' ? 'customer' : 'vendor'} fills the fields below — you can still edit them for this invoice only.
                </p>
              </div>
              <div className="space-y-2">
                <Label>{formData.direction === 'Outgoing' ? 'Client Name *' : 'Vendor Name *'}</Label>
                <Input value={formData.party_name} onChange={(e) => setFormData({ ...formData, party_name: e.target.value, party_id: null })} data-testid="form-party-name" required />
              </div>
              <div className="space-y-2">
                <Label>Email</Label>
                <Input type="email" value={formData.party_email} onChange={(e) => setFormData({ ...formData, party_email: e.target.value })} />
              </div>
              <div className="space-y-2">
                <Label>Phone</Label>
                <Input value={formData.party_phone} onChange={(e) => setFormData({ ...formData, party_phone: e.target.value })} />
              </div>
              <div className="space-y-2">
                <Label>Tax ID (TIN/VAT)</Label>
                <Input value={formData.party_tax_id} onChange={(e) => setFormData({ ...formData, party_tax_id: e.target.value })} />
              </div>
              <div className="sm:col-span-2 space-y-2">
                <Label>Address</Label>
                <Textarea rows={2} value={formData.party_address} onChange={(e) => setFormData({ ...formData, party_address: e.target.value })} />
              </div>
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              <div className="space-y-2">
                <Label>Issue Date *</Label>
                <Input type="date" value={formData.issue_date} onChange={(e) => setFormData({ ...formData, issue_date: e.target.value })} required />
              </div>
              <div className="space-y-2">
                <Label>Due Date</Label>
                <Input type="date" value={formData.due_date} onChange={(e) => setFormData({ ...formData, due_date: e.target.value })} />
              </div>
              <div className="space-y-2">
                <Label>Currency</Label>
                <Input
                  value={formData.currency}
                  readOnly
                  disabled
                  className="bg-muted/40 cursor-not-allowed"
                  title="Currency is set in office configuration"
                />
              </div>
              <div className="space-y-2">
                <Label className="invisible">VAT</Label>
                <label className="flex items-center gap-2 px-3 py-2 rounded-md border bg-muted/20 cursor-pointer hover:bg-muted/40 transition-colors h-10">
                  <input
                    type="checkbox"
                    className="h-4 w-4 accent-blue-600"
                    checked={formData.apply_vat}
                    onChange={(e) => {
                      const checked = e.target.checked;
                      setFormData({
                        ...formData,
                        apply_vat: checked,
                        tax_percent: checked ? (officeConfig.vat_percent ?? 18) : 0,
                      });
                    }}
                    data-testid="apply-vat-checkbox"
                  />
                  <span className="text-sm">
                    Apply VAT {formData.apply_vat ? `(${officeConfig.vat_percent ?? 18}%)` : ''}
                  </span>
                </label>
              </div>
            </div>

            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label className="text-base">Line Items *</Label>
                <Button type="button" size="sm" variant="outline" onClick={addLine} className="gap-1"><Plus className="h-3 w-3" /> Add Line</Button>
              </div>
              <div className="space-y-2">
                {/* Header row */}
                <div className="grid grid-cols-12 gap-2 px-1 text-xs font-medium uppercase tracking-wide text-muted-foreground">
                  <div className="col-span-6">Item</div>
                  <div className="col-span-2 text-right">Quantity</div>
                  <div className="col-span-3 text-right">Rate</div>
                  <div className="col-span-1"></div>
                </div>
                {formData.line_items.map((line, idx) => (
                  <div key={idx} className="grid grid-cols-12 gap-2 items-start" data-testid={`line-item-${idx}`}>
                    <div className="col-span-6"><Input placeholder="Item description" value={line.description} onChange={(e) => updateLine(idx, 'description', e.target.value)} /></div>
                    <div className="col-span-2"><Input type="number" step="0.01" placeholder="Qty" value={line.quantity} onChange={(e) => updateLine(idx, 'quantity', e.target.value)} /></div>
                    <div className="col-span-3"><Input type="number" step="0.01" placeholder="Rate" value={line.unit_price} onChange={(e) => updateLine(idx, 'unit_price', e.target.value)} /></div>
                    <div className="col-span-1 flex items-center justify-end pt-2">
                      {formData.line_items.length > 1 && (
                        <Button type="button" size="icon" variant="ghost" onClick={() => removeLine(idx)} className="h-8 w-8 text-red-600">
                          <X className="h-4 w-4" />
                        </Button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
              <div className="bg-muted/40 rounded-lg p-3 text-sm space-y-1">
                <div className="flex justify-between"><span>Subtotal</span><span className="font-medium">{fmt(formData.currency, computedTotals.subtotal)}</span></div>
                {formData.apply_vat && (
                  <div className="flex justify-between"><span>VAT ({Number(formData.tax_percent) || 0}%)</span><span className="font-medium">{fmt(formData.currency, computedTotals.tax)}</span></div>
                )}
                <div className="flex justify-between border-t pt-1 text-base font-bold text-blue-600"><span>Total</span><span>{fmt(formData.currency, computedTotals.total)}</span></div>
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Payment Terms</Label>
                <Input value={formData.payment_terms} onChange={(e) => setFormData({ ...formData, payment_terms: e.target.value })} placeholder="e.g. Net 30 days" />
              </div>
              <div className="space-y-2">
                <Label>Notes</Label>
                <Textarea rows={2} value={formData.notes} onChange={(e) => setFormData({ ...formData, notes: e.target.value })} />
              </div>
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => { setShowFormModal(false); resetForm(); }} disabled={submitting}>Cancel</Button>
              <Button type="submit" disabled={submitting} data-testid="form-submit">
                {submitting && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
                {editing ? 'Save Changes' : 'Create Invoice'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Detail modal */}
      <Dialog open={showDetail} onOpenChange={(o) => { if (!o) setShowDetail(false); }}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Invoice {selectedInvoice?.invoice_number}</DialogTitle></DialogHeader>
          {selectedInvoice && (
            <div className="space-y-4">
              <div className="flex items-center gap-2">
                {STATUS_BADGE(selectedInvoice.status, selectedInvoice.is_overdue)}
                <Badge variant="outline">{selectedInvoice.direction}</Badge>
              </div>
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div><div className="text-xs text-muted-foreground">Party</div><div className="font-medium">{selectedInvoice.party_name}</div></div>
                <div><div className="text-xs text-muted-foreground">Office</div><div>{selectedInvoice.office_name || '—'}</div></div>
                <div><div className="text-xs text-muted-foreground">Issue</div><div>{selectedInvoice.issue_date}</div></div>
                <div><div className="text-xs text-muted-foreground">Due</div><div>{selectedInvoice.due_date || '—'}</div></div>
              </div>
              <div className="border rounded-lg overflow-hidden">
                <table className="w-full text-sm">
                  <thead className="bg-muted/40">
                    <tr><th className="text-left px-3 py-2">Item</th><th className="text-right px-3 py-2">Quantity</th><th className="text-right px-3 py-2">Rate</th><th className="text-right px-3 py-2">Amount</th></tr>
                  </thead>
                  <tbody>
                    {selectedInvoice.line_items?.map((l, i) => (
                      <tr key={i} className="border-t">
                        <td className="px-3 py-2">{l.description}</td>
                        <td className="px-3 py-2 text-right">{l.quantity}</td>
                        <td className="px-3 py-2 text-right">{l.unit_price?.toLocaleString()}</td>
                        <td className="px-3 py-2 text-right">{(l.quantity * l.unit_price).toLocaleString()}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="bg-muted/30 rounded-lg p-3 space-y-1 text-sm">
                <div className="flex justify-between"><span>Subtotal</span><span>{fmt(selectedInvoice.currency, selectedInvoice.subtotal)}</span></div>
                {(selectedInvoice.tax_percent || 0) > 0 && (
                  <div className="flex justify-between"><span>VAT ({selectedInvoice.tax_percent}%)</span><span>{fmt(selectedInvoice.currency, selectedInvoice.tax_amount)}</span></div>
                )}
                <div className="flex justify-between font-bold border-t pt-1"><span>Total</span><span>{fmt(selectedInvoice.currency, selectedInvoice.total_amount)}</span></div>
                <div className="flex justify-between"><span>Paid</span><span className="text-emerald-600">{fmt(selectedInvoice.currency, selectedInvoice.paid_amount)}</span></div>
                <div className="flex justify-between font-bold"><span>Balance</span><span className={selectedInvoice.balance > 0 ? 'text-amber-700' : 'text-emerald-600'}>{fmt(selectedInvoice.currency, selectedInvoice.balance)}</span></div>
              </div>

              <div>
                <div className="flex items-center justify-between mb-2">
                  <h3 className="font-semibold flex items-center gap-2"><CreditCard className="h-4 w-4" /> Payment History ({selectedInvoice.payments?.length || 0})</h3>
                  {selectedInvoice.balance > 0 && (
                    <Button size="sm" onClick={() => { setShowDetail(false); openPaymentModal(selectedInvoice); }} className="gap-1"><Plus className="h-3 w-3" /> Record Payment</Button>
                  )}
                </div>
                {selectedInvoice.payments?.length === 0 ? (
                  <p className="text-sm text-muted-foreground">No payments recorded yet.</p>
                ) : (
                  <div className="border rounded-lg divide-y">
                    {selectedInvoice.payments?.map((p) => (
                      <div key={p.id} className="p-3 flex items-start justify-between gap-3" data-testid={`payment-row-${p.id}`}>
                        <div className="flex-1">
                          <div className="font-medium">{fmt(selectedInvoice.currency, p.amount)} <span className="text-xs text-muted-foreground font-normal">via {p.method || '—'}</span></div>
                          <div className="text-xs text-muted-foreground">{p.payment_date} {p.reference && `· Ref: ${p.reference}`}</div>
                          {p.notes && <div className="text-xs text-muted-foreground mt-1">{p.notes}</div>}
                          {p.recorded_by_name && <div className="text-[11px] text-muted-foreground mt-1">Recorded by {p.recorded_by_name}</div>}
                        </div>
                        <Button variant="ghost" size="icon" className="h-7 w-7 text-red-600" onClick={() => deletePayment(p.id)} title="Remove payment"><Trash2 className="h-3.5 w-3.5" /></Button>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {selectedInvoice.payment_terms && (
                <div className="text-sm"><div className="text-xs text-muted-foreground">Payment Terms</div><div>{selectedInvoice.payment_terms}</div></div>
              )}
              {selectedInvoice.notes && (
                <div className="text-sm"><div className="text-xs text-muted-foreground">Notes</div><div>{selectedInvoice.notes}</div></div>
              )}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDetail(false)}>Close</Button>
            {selectedInvoice?.direction === 'Outgoing' && (
              <Button onClick={() => downloadPdf(selectedInvoice)} className="gap-2"><Download className="h-4 w-4" /> Download PDF</Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Payment modal */}
      <Dialog open={showPaymentModal} onOpenChange={(o) => { if (!o) setShowPaymentModal(false); }}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>Record Payment</DialogTitle></DialogHeader>
          <form onSubmit={recordPayment} className="space-y-3">
            {selectedInvoice && (
              <div className="bg-muted/30 rounded-lg p-3 text-sm">
                <div>{selectedInvoice.invoice_number} — {selectedInvoice.party_name}</div>
                <div className="text-xs text-muted-foreground mt-1">Outstanding: <span className="font-bold text-amber-700">{fmt(selectedInvoice.currency, selectedInvoice.balance)}</span></div>
              </div>
            )}
            <div className="space-y-2">
              <Label>Amount *</Label>
              <Input type="number" step="0.01" value={paymentForm.amount} onChange={(e) => setPaymentForm({ ...paymentForm, amount: e.target.value })} required data-testid="payment-amount" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label>Date *</Label>
                <Input type="date" value={paymentForm.payment_date} onChange={(e) => setPaymentForm({ ...paymentForm, payment_date: e.target.value })} required />
              </div>
              <div className="space-y-2">
                <Label>Method</Label>
                <Select value={paymentForm.method} onValueChange={(v) => setPaymentForm({ ...paymentForm, method: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Bank Transfer">Bank Transfer</SelectItem>
                    <SelectItem value="Cash">Cash</SelectItem>
                    <SelectItem value="Mobile Money">Mobile Money</SelectItem>
                    <SelectItem value="Cheque">Cheque</SelectItem>
                    <SelectItem value="Credit Card">Credit Card</SelectItem>
                    <SelectItem value="Other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-2">
              <Label>Reference</Label>
              <Input value={paymentForm.reference} onChange={(e) => setPaymentForm({ ...paymentForm, reference: e.target.value })} placeholder="Bank ref, M-Pesa code..." />
            </div>
            <div className="space-y-2">
              <Label>Notes</Label>
              <Textarea rows={2} value={paymentForm.notes} onChange={(e) => setPaymentForm({ ...paymentForm, notes: e.target.value })} />
            </div>
            {selectedInvoice?.direction === 'Incoming' && (
              <div className="space-y-2">
                <Label>Proof of Payment</Label>
                <Input
                  type="file"
                  accept="application/pdf,image/png,image/jpeg,image/jpg"
                  onChange={(e) => setPaymentProofFile(e.target.files?.[0] || null)}
                  data-testid="payment-proof-input"
                />
                {paymentProofFile && (
                  <div className="flex items-center justify-between text-xs text-muted-foreground bg-muted/40 rounded px-2 py-1.5">
                    <span className="truncate flex items-center gap-1.5">
                      <FileText className="h-3.5 w-3.5" />
                      {paymentProofFile.name}
                    </span>
                    <button
                      type="button"
                      onClick={() => setPaymentProofFile(null)}
                      className="text-red-600 hover:text-red-700"
                      data-testid="payment-proof-clear"
                    >
                      <X className="h-3.5 w-3.5" />
                    </button>
                  </div>
                )}
                <p className="text-[11px] text-muted-foreground">
                  Optional. Attach a receipt, bank slip, or screenshot (PDF or image, max ~5MB).
                </p>
              </div>
            )}
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowPaymentModal(false)} disabled={submitting}>Cancel</Button>
              <Button type="submit" disabled={submitting} data-testid="payment-submit">
                {submitting && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
                Save Payment
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <AttachmentPreviewModal
        open={!!previewConfig}
        onClose={() => setPreviewConfig(null)}
        title={previewConfig?.title || ''}
        downloadName={previewConfig?.downloadName || 'invoice.pdf'}
        fetchUrl={previewConfig?.fetchUrl || ''}
        headers={getAuthHeaders()}
        mime={previewConfig?.mime}
      />

      {/* Quick-add / Edit Customer/Vendor modal */}
      <Dialog open={showPartyModal} onOpenChange={(o) => { if (!o) { setShowPartyModal(false); setEditingParty(null); } }}>
        <DialogContent className="max-w-md" data-testid="party-quickadd-modal">
          <DialogHeader>
            <DialogTitle>
              {editingParty ? `Edit ${partyForm.kind}` : `Add ${partyForm.kind}`}
            </DialogTitle>
          </DialogHeader>
          <form
            onSubmit={async (e) => {
              e.preventDefault();
              if (!partyForm.name.trim()) { toast.error('Name is required'); return; }
              setPartySubmitting(true);
              try {
                const headers = { ...getAuthHeaders(), 'Content-Type': 'application/json' };
                const body = {
                  kind: partyForm.kind,
                  name: partyForm.name.trim(),
                  email: partyForm.email || null,
                  phone: partyForm.phone || null,
                  address: partyForm.address || null,
                  tax_id: partyForm.tax_id || null,
                  notes: partyForm.notes || null,
                };
                if (!editingParty && isSA && formData.office_id) body.office_id = formData.office_id;
                const url = editingParty
                  ? `${API_URL}/api/invoice-parties/${editingParty.id}`
                  : `${API_URL}/api/invoice-parties/`;
                const res = await fetch(url, {
                  method: editingParty ? 'PUT' : 'POST',
                  headers,
                  body: JSON.stringify(body),
                });
                if (res.ok) {
                  const saved = await res.json();
                  toast.success(`${saved.kind} ${editingParty ? 'updated' : 'added'}`);
                  await fetchParties();
                  // Auto-select only when adding from invoice form (not from directory)
                  if (!editingParty && !showDirectoryModal) {
                    setFormData((prev) => ({
                      ...prev,
                      party_id: saved.id,
                      party_name: saved.name || '',
                      party_email: saved.email || '',
                      party_phone: saved.phone || '',
                      party_address: saved.address || '',
                      party_tax_id: saved.tax_id || '',
                    }));
                  }
                  setShowPartyModal(false);
                  setEditingParty(null);
                } else {
                  const err = await res.json().catch(() => ({}));
                  toast.error(err.detail || 'Failed to save');
                }
              } catch { toast.error('Network error'); }
              finally { setPartySubmitting(false); }
            }}
            className="space-y-3"
          >
            <div className="space-y-2">
              <Label>Name *</Label>
              <Input
                value={partyForm.name}
                onChange={(e) => setPartyForm({ ...partyForm, name: e.target.value })}
                required
                autoFocus
                data-testid="party-name-input"
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label>Email</Label>
                <Input type="email" value={partyForm.email} onChange={(e) => setPartyForm({ ...partyForm, email: e.target.value })} />
              </div>
              <div className="space-y-2">
                <Label>Phone</Label>
                <Input value={partyForm.phone} onChange={(e) => setPartyForm({ ...partyForm, phone: e.target.value })} />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Tax ID (TIN/VAT)</Label>
              <Input value={partyForm.tax_id} onChange={(e) => setPartyForm({ ...partyForm, tax_id: e.target.value })} />
            </div>
            <div className="space-y-2">
              <Label>Address</Label>
              <Textarea rows={2} value={partyForm.address} onChange={(e) => setPartyForm({ ...partyForm, address: e.target.value })} />
            </div>
            <div className="space-y-2">
              <Label>Notes</Label>
              <Textarea rows={2} value={partyForm.notes} onChange={(e) => setPartyForm({ ...partyForm, notes: e.target.value })} />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => { setShowPartyModal(false); setEditingParty(null); }} disabled={partySubmitting}>Cancel</Button>
              <Button type="submit" disabled={partySubmitting} data-testid="party-quickadd-submit">
                {partySubmitting && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
                {editingParty ? 'Update' : `Save ${partyForm.kind}`}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Customers & Vendors Directory modal */}
      <Dialog open={showDirectoryModal} onOpenChange={(o) => { if (!o) setShowDirectoryModal(false); }}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto" data-testid="directory-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Users className="h-5 w-5 text-blue-600" />
              Customers &amp; Vendors Directory
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div className="flex flex-wrap items-center gap-2">
              <Tabs value={directoryTab} onValueChange={setDirectoryTab}>
                <TabsList>
                  <TabsTrigger value="Customer" data-testid="directory-tab-customer">Customers</TabsTrigger>
                  <TabsTrigger value="Vendor" data-testid="directory-tab-vendor">Vendors</TabsTrigger>
                </TabsList>
              </Tabs>
              <div className="relative flex-1 min-w-[180px]">
                <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                <Input
                  className="pl-9"
                  placeholder="Search by name, email, TIN…"
                  value={directorySearch}
                  onChange={(e) => setDirectorySearch(e.target.value)}
                  data-testid="directory-search"
                />
              </div>
              <Button
                onClick={() => {
                  setEditingParty(null);
                  setPartyForm({
                    kind: directoryTab,
                    name: '', email: '', phone: '', address: '', tax_id: '', notes: '',
                  });
                  setShowPartyModal(true);
                }}
                className="gap-1"
                data-testid="directory-new-btn"
              >
                <Plus className="h-4 w-4" /> New {directoryTab}
              </Button>
            </div>

            {(() => {
              const filtered = parties.filter((p) => p.kind === directoryTab).filter((p) => {
                if (!directorySearch.trim()) return true;
                const q = directorySearch.trim().toLowerCase();
                return (
                  (p.name || '').toLowerCase().includes(q) ||
                  (p.email || '').toLowerCase().includes(q) ||
                  (p.phone || '').toLowerCase().includes(q) ||
                  (p.tax_id || '').toLowerCase().includes(q)
                );
              });
              if (filtered.length === 0) {
                return (
                  <div className="border border-dashed rounded-lg p-10 text-center text-muted-foreground">
                    <Users className="h-10 w-10 mx-auto opacity-40 mb-2" />
                    <p className="text-sm font-medium">
                      No {directoryTab.toLowerCase()}s yet
                    </p>
                    <p className="text-xs mt-1">
                      Add your first {directoryTab.toLowerCase()} to start using them on invoices.
                    </p>
                  </div>
                );
              }
              return (
                <div className="border rounded-lg overflow-hidden">
                  <table className="w-full text-sm">
                    <thead className="bg-muted/40 text-muted-foreground">
                      <tr className="text-left">
                        <th className="px-3 py-2 font-medium">Name</th>
                        <th className="px-3 py-2 font-medium">Contact</th>
                        <th className="px-3 py-2 font-medium">TIN</th>
                        <th className="px-3 py-2 font-medium text-right">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filtered.map((p) => (
                        <tr key={p.id} className="border-t hover:bg-muted/20" data-testid={`directory-row-${p.id}`}>
                          <td className="px-3 py-2">
                            <div className="font-medium">{p.name}</div>
                            {p.address && (
                              <div className="text-[11px] text-muted-foreground line-clamp-1">{p.address}</div>
                            )}
                          </td>
                          <td className="px-3 py-2 text-xs">
                            {p.email && <div>{p.email}</div>}
                            {p.phone && <div className="text-muted-foreground">{p.phone}</div>}
                            {!p.email && !p.phone && <span className="text-muted-foreground">—</span>}
                          </td>
                          <td className="px-3 py-2 text-xs font-mono text-muted-foreground">
                            {p.tax_id || '—'}
                          </td>
                          <td className="px-3 py-2">
                            <div className="flex justify-end gap-1">
                              <Button
                                variant="ghost" size="icon" className="h-7 w-7"
                                onClick={() => {
                                  setEditingParty(p);
                                  setPartyForm({
                                    kind: p.kind,
                                    name: p.name || '',
                                    email: p.email || '',
                                    phone: p.phone || '',
                                    address: p.address || '',
                                    tax_id: p.tax_id || '',
                                    notes: p.notes || '',
                                  });
                                  setShowPartyModal(true);
                                }}
                                title="Edit"
                                data-testid={`directory-edit-${p.id}`}
                              >
                                <Edit className="h-3.5 w-3.5" />
                              </Button>
                              <Button
                                variant="ghost" size="icon"
                                className="h-7 w-7 text-red-600 hover:text-red-700"
                                onClick={async () => {
                                  if (!window.confirm(`Remove ${p.kind.toLowerCase()} "${p.name}"?`)) return;
                                  try {
                                    const res = await fetch(`${API_URL}/api/invoice-parties/${p.id}`, {
                                      method: 'DELETE', headers: getAuthHeaders(),
                                    });
                                    if (res.ok) {
                                      toast.success('Removed');
                                      fetchParties();
                                    } else {
                                      toast.error('Failed to remove');
                                    }
                                  } catch { toast.error('Network error'); }
                                }}
                                title="Delete"
                                data-testid={`directory-delete-${p.id}`}
                              >
                                <Trash2 className="h-3.5 w-3.5" />
                              </Button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              );
            })()}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDirectoryModal(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Payment Log Modal — focused payment history view */}
      <Dialog open={!!paymentLogInvoice} onOpenChange={(o) => { if (!o) setPaymentLogInvoice(null); }}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="payment-log-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <History className="h-5 w-5 text-emerald-600" />
              Payment Log
            </DialogTitle>
          </DialogHeader>
          {paymentLogInvoice && (
            <div className="space-y-4">
              {/* Invoice summary header */}
              <div className="bg-muted/30 rounded-lg p-4 space-y-2">
                <div className="flex items-center justify-between flex-wrap gap-2">
                  <div>
                    <div className="font-mono text-xs text-muted-foreground">{paymentLogInvoice.invoice_number}</div>
                    <div className="font-semibold mt-0.5">{paymentLogInvoice.party_name}</div>
                  </div>
                  <div className="flex items-center gap-2">
                    {STATUS_BADGE(paymentLogInvoice.status, paymentLogInvoice.is_overdue)}
                    <Badge variant="outline">{paymentLogInvoice.direction}</Badge>
                  </div>
                </div>
                <div className="grid grid-cols-3 gap-3 text-sm pt-2 border-t border-muted">
                  <div>
                    <div className="text-[11px] text-muted-foreground uppercase tracking-wide">Total</div>
                    <div className="font-semibold">{fmt(paymentLogInvoice.currency, paymentLogInvoice.total_amount)}</div>
                  </div>
                  <div>
                    <div className="text-[11px] text-muted-foreground uppercase tracking-wide">Paid</div>
                    <div className="font-semibold text-emerald-600">{fmt(paymentLogInvoice.currency, paymentLogInvoice.paid_amount)}</div>
                  </div>
                  <div>
                    <div className="text-[11px] text-muted-foreground uppercase tracking-wide">Balance</div>
                    <div className={`font-bold ${paymentLogInvoice.balance > 0 ? 'text-amber-700' : 'text-emerald-600'}`}>
                      {fmt(paymentLogInvoice.currency, paymentLogInvoice.balance)}
                    </div>
                  </div>
                </div>
              </div>

              {/* Payment rows */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <h3 className="text-sm font-semibold">
                    Recorded Payments ({paymentLogInvoice.payments?.length || 0})
                  </h3>
                  {paymentLogInvoice.balance > 0 && paymentLogInvoice.status !== 'Cancelled' && (
                    <Button
                      size="sm"
                      onClick={() => {
                        setPaymentLogInvoice(null);
                        openPaymentModal(paymentLogInvoice);
                      }}
                      className="gap-1"
                      data-testid="add-payment-from-log"
                    >
                      <Plus className="h-3.5 w-3.5" /> Record Payment
                    </Button>
                  )}
                </div>
                {(!paymentLogInvoice.payments || paymentLogInvoice.payments.length === 0) ? (
                  <div className="border border-dashed rounded-lg p-8 text-center text-muted-foreground">
                    <CreditCard className="h-8 w-8 mx-auto mb-2 opacity-40" />
                    <p className="text-sm font-medium">No payments recorded yet</p>
                    <p className="text-xs mt-0.5">
                      {paymentLogInvoice.direction === 'Outgoing'
                        ? 'When the client pays, log it here to track outstanding balance.'
                        : 'When you pay this vendor invoice, log it here to track outstanding balance.'}
                    </p>
                  </div>
                ) : (
                  <div className="border rounded-lg overflow-hidden">
                    <div className="bg-muted/40 grid grid-cols-12 gap-2 px-3 py-2 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                      <div className="col-span-3">Date</div>
                      <div className="col-span-3 text-right">Amount</div>
                      <div className="col-span-3">Method</div>
                      <div className="col-span-2">Reference</div>
                      <div className="col-span-1"></div>
                    </div>
                    <div className="divide-y">
                      {paymentLogInvoice.payments.map((p) => (
                        <div key={p.id} className="grid grid-cols-12 gap-2 px-3 py-3 items-start text-sm hover:bg-muted/20" data-testid={`log-payment-${p.id}`}>
                          <div className="col-span-3">
                            <div className="flex items-center gap-1.5">
                              <Calendar className="h-3 w-3 opacity-60" />
                              <span>{p.payment_date}</span>
                            </div>
                            {p.recorded_by_name && (
                              <div className="text-[11px] text-muted-foreground mt-1">by {p.recorded_by_name}</div>
                            )}
                          </div>
                          <div className="col-span-3 text-right font-semibold text-emerald-700">
                            {fmt(paymentLogInvoice.currency, p.amount)}
                          </div>
                          <div className="col-span-3">
                            <Badge variant="outline" className="text-[11px] font-normal">
                              {p.method || 'Unspecified'}
                            </Badge>
                          </div>
                          <div className="col-span-2 text-xs font-mono text-muted-foreground break-all">
                            {p.reference || '—'}
                          </div>
                          <div className="col-span-1 flex justify-end items-center gap-1">
                            {p.has_proof && (
                              <Button
                                variant="ghost" size="icon"
                                className="h-7 w-7 text-blue-600 hover:text-blue-700"
                                onClick={() => setPreviewConfig({
                                  title: `Payment Proof — ${fmt(paymentLogInvoice.currency, p.amount)}`,
                                  downloadName: p.proof_filename || `payment-proof-${p.id}.pdf`,
                                  fetchUrl: `${API_URL}/api/invoices/${paymentLogInvoice.id}/payments/${p.id}/proof`,
                                  mime: p.proof_mime,
                                })}
                                title="View proof of payment"
                                data-testid={`log-view-proof-${p.id}`}
                              >
                                <Eye className="h-3.5 w-3.5" />
                              </Button>
                            )}
                            <Button
                              variant="ghost" size="icon"
                              className="h-7 w-7 text-red-600 hover:text-red-700"
                              onClick={() => deletePaymentFromLog(p.id)}
                              title="Remove payment"
                              data-testid={`log-delete-payment-${p.id}`}
                            >
                              <Trash2 className="h-3.5 w-3.5" />
                            </Button>
                          </div>
                          {p.notes && (
                            <div className="col-span-12 text-xs text-muted-foreground italic pl-1 -mt-1">
                              "{p.notes}"
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setPaymentLogInvoice(null)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Invoices;
